"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { AlertTriangle, Pencil, Trash2, X } from "lucide-react";
import { invalidarCacheCliente } from "@/lib/fetch-cache-cliente";
import { exibirPercentual, percentualParaBasisPoints, validarPercentuaisParaSalvar, type PesoNumeroInput } from "@/lib/followpro-percentuais-input";
import { somenteDigitosTelefone } from "@/lib/validacoes-usuario-equipe";
import { Button } from "./Button";
import { Dialog } from "./Dialog";
import { Input } from "./Input";
import { LoadingIndicator } from "./LoadingIndicator";

type Numero = { id: string; posicao: number; telefone: string };
type RespostaPercentuais = { numeros: Numero[]; pesos: PesoNumeroInput[]; configurada: boolean };
type ListaVinculada = {
  listaLeadId: string;
  listaNome: string;
  ativo: boolean;
  bloqueia: boolean;
  motivos: string[];
};

const LIMITE = 3;

async function lerResposta<T>(resposta: Response, mensagem: string): Promise<T> {
  const dados = await resposta.json().catch(() => ({})) as T & { erro?: string };
  if (!resposta.ok) throw new Error(dados.erro ?? mensagem);
  return dados;
}

export function PopupNumeros({ followPro, onClose }: { followPro: boolean; onClose: () => void }) {
  const router = useRouter();
  const [numeros, setNumeros] = useState<Numero[]>([]);
  const [persistidos, setPersistidos] = useState<Numero[]>([]);
  const [editando, setEditando] = useState<number | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [falhaCarregamento, setFalhaCarregamento] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [numeroRemocao, setNumeroRemocao] = useState<Numero | null>(null);
  const [listas, setListas] = useState<ListaVinculada[]>([]);
  const [carregandoRemocao, setCarregandoRemocao] = useState(false);
  const [removendo, setRemovendo] = useState(false);
  const [erroRemocao, setErroRemocao] = useState("");
  const consultaRemocao = useRef(0);
  const [rascunhos, setRascunhos] = useState<Record<string, string>>({});
  const [configurada, setConfigurada] = useState(false);
  const [carregandoPesos, setCarregandoPesos] = useState(false);
  const [salvandoPesos, setSalvandoPesos] = useState(false);
  const [erroPesos, setErroPesos] = useState("");
  const [falhaLeituraPesos, setFalhaLeituraPesos] = useState(false);
  const [sucessoPesos, setSucessoPesos] = useState("");

  function aplicarPercentuais(dados: RespostaPercentuais) {
    const ordenados = [...dados.numeros].sort((a, b) => a.posicao - b.posicao);
    setNumeros(ordenados);
    setPersistidos(ordenados);
    setRascunhos(Object.fromEntries(dados.pesos.map((peso) => [peso.numeroOperacionalId, exibirPercentual(peso.pesoBasisPoints)])));
    setConfigurada(dados.configurada);
    setFalhaLeituraPesos(false);
    setEditando(null);
  }

  async function recarregarPercentuais() {
    if (!followPro) return;
    setCarregandoPesos(true);
    setErroPesos("");
    setSucessoPesos("");
    try {
      const dados = await lerResposta<RespostaPercentuais>(
        await fetch("/api/numeros-operacionais/percentuais", { cache: "no-store" }),
        "Não foi possível carregar os percentuais.",
      );
      aplicarPercentuais(dados);
    } catch (falha) {
      setConfigurada(false);
      setFalhaLeituraPesos(true);
      setErroPesos(falha instanceof Error ? falha.message : "Não foi possível carregar os percentuais.");
    } finally {
      setCarregandoPesos(false);
    }
  }

  useEffect(() => {
    const controller = new AbortController();
    fetch(followPro ? "/api/numeros-operacionais/percentuais" : "/api/numeros-operacionais", { cache: "no-store", signal: controller.signal })
      .then((resposta) => lerResposta<RespostaPercentuais>(resposta, "Não foi possível carregar os números."))
      .then((dados) => {
        if (controller.signal.aborted) return;
        if (followPro) aplicarPercentuais(dados);
        else {
          const ordenados = [...dados.numeros].sort((a, b) => a.posicao - b.posicao);
          setNumeros(ordenados);
          setPersistidos(ordenados);
        }
      })
      .catch((falha) => {
        if (!controller.signal.aborted) {
          setFalhaCarregamento(true);
          setErro(falha instanceof Error ? falha.message : "Não foi possível carregar os números.");
        }
      })
      .finally(() => { if (!controller.signal.aborted) setCarregando(false); });
    return () => controller.abort();
  }, [followPro]);

  function adicionar() {
    if (numeros.length >= LIMITE) return;
    const posicao = [1, 2, 3].find((item) => !numeros.some((numero) => numero.posicao === item));
    if (!posicao) return;
    setNumeros((atuais) => [...atuais, { id: "", posicao, telefone: "" }].sort((a, b) => a.posicao - b.posicao));
    setEditando(posicao);
    setErro("");
    setSucesso("");
    setSucessoPesos("");
  }

  async function salvar() {
    if (salvando || carregando || falhaCarregamento) return;
    const maiorPosicao = numeros.reduce((maior, numero) => Math.max(maior, numero.posicao), 0);
    const telefones = Array.from({ length: maiorPosicao }, (_, indice) =>
      numeros.find((numero) => numero.posicao === indice + 1)?.telefone ?? "",
    );
    setSalvando(true);
    setErro("");
    setSucesso("");
    try {
      const dados = await lerResposta<{ numeros: Numero[] }>(await fetch("/api/numeros-operacionais", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ numeros: telefones }),
      }), "Não foi possível salvar os números.");
      const ordenados = [...dados.numeros].sort((a, b) => a.posicao - b.posicao);
      setNumeros(ordenados);
      setPersistidos(ordenados);
      setEditando(null);
      setSucesso("Números salvos.");
      invalidarCacheCliente("/api/numeros-operacionais");
      if (followPro) await recarregarPercentuais();
    } catch (falha) {
      setNumeros([...persistidos]);
      setEditando(null);
      setErro(falha instanceof Error ? falha.message : "Não foi possível salvar os números.");
    } finally {
      setSalvando(false);
    }
  }

  async function abrirRemocao(numero: Numero) {
    if (!numero.id) {
      setNumeros((atuais) => atuais.filter((atual) => atual.posicao !== numero.posicao));
      setEditando(null);
      setErro("");
      setSucesso("");
      setSucessoPesos("");
      return;
    }
    setNumeroRemocao(numero);
    const consulta = ++consultaRemocao.current;
    setListas([]);
    setErroRemocao("");
    setCarregandoRemocao(true);
    try {
      const dados = await lerResposta<{ listas: ListaVinculada[] }>(
        await fetch(`/api/numeros-operacionais/${numero.id}/remocao`, { cache: "no-store" }),
        "Não foi possível carregar as Listas vinculadas.",
      );
      if (consulta === consultaRemocao.current) setListas(dados.listas);
    } catch (falha) {
      if (consulta === consultaRemocao.current) setErroRemocao(falha instanceof Error ? falha.message : "Não foi possível carregar as Listas vinculadas.");
    } finally {
      if (consulta === consultaRemocao.current) setCarregandoRemocao(false);
    }
  }

  function fecharRemocao() {
    if (removendo) return;
    consultaRemocao.current += 1;
    setNumeroRemocao(null);
    setListas([]);
    setCarregandoRemocao(false);
    setErroRemocao("");
  }

  async function confirmarRemocao() {
    if (!numeroRemocao || carregandoRemocao || removendo || erroRemocao || listas.some((lista) => lista.bloqueia)) return;
    setRemovendo(true);
    setErroRemocao("");
    try {
      const resposta = await fetch(`/api/numeros-operacionais/${numeroRemocao.id}/remocao`, { method: "DELETE" });
      const dados = await resposta.json().catch(() => ({})) as { erro?: string; bloqueios?: ListaVinculada[] };
      if (!resposta.ok) {
        if (Array.isArray(dados.bloqueios)) setListas(dados.bloqueios);
        throw new Error(dados.erro ?? "Não foi possível remover o número operacional.");
      }
      setNumeros((atuais) => atuais.filter((numero) => numero.id !== numeroRemocao.id));
      setPersistidos((atuais) => atuais.filter((numero) => numero.id !== numeroRemocao.id));
      setEditando((atual) => atual === numeroRemocao.posicao ? null : atual);
      setNumeroRemocao(null);
      setListas([]);
      setSucesso("Número removido.");
      invalidarCacheCliente("/api/numeros-operacionais");
      if (followPro) await recarregarPercentuais();
    } catch (falha) {
      setErroRemocao(falha instanceof Error ? falha.message : "Não foi possível remover o número operacional.");
    } finally {
      setRemovendo(false);
    }
  }

  async function salvarPercentuais() {
    if (!followPro || salvandoPesos || carregandoPesos || numerosPendentes) return;
    const validacao = validarPercentuaisParaSalvar(numeros, rascunhos);
    if ("erro" in validacao) { setErroPesos(validacao.erro); return; }
    setSalvandoPesos(true);
    setErroPesos("");
    setSucessoPesos("");
    try {
      const dados = await lerResposta<{ ok: boolean; pesos: PesoNumeroInput[] }>(await fetch("/api/numeros-operacionais/percentuais", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pesos: validacao.pesos }),
      }), "Não foi possível salvar os percentuais.");
      if (!dados.ok) throw new Error("Não foi possível salvar os percentuais.");
      setRascunhos(Object.fromEntries(dados.pesos.map((peso) => [peso.numeroOperacionalId, exibirPercentual(peso.pesoBasisPoints)])));
      setConfigurada(true);
      setSucessoPesos("Percentuais salvos para os próximos Leads.");
    } catch (falha) {
      setErroPesos(falha instanceof Error ? falha.message : "Não foi possível salvar os percentuais.");
    } finally {
      setSalvandoPesos(false);
    }
  }

  const numerosPendentes = numeros.length !== persistidos.length || numeros.some((numero) =>
    !numero.id || !persistidos.some((persistido) => persistido.id === numero.id && persistido.telefone === numero.telefone),
  );
  const validacaoPesos = followPro && numeros.length > 1 ? validarPercentuaisParaSalvar(numeros, rascunhos) : null;
  const somaPesos = numeros.reduce((total, numero) => total + (percentualParaBasisPoints(rascunhos[numero.id] ?? "") ?? 0), 0);
  const ocupado = salvando || removendo || salvandoPesos || carregandoPesos;

  return (
    <>
      <Dialog
        open
        onOpenChange={(aberto) => { if (!aberto && !ocupado && !numeroRemocao) onClose(); }}
        closeOnEscape={!ocupado && !numeroRemocao}
        closeOnBackdrop={!ocupado && !numeroRemocao}
        aria-labelledby="titulo-popup-numeros"
      >
        <div className="flex items-center justify-between gap-3">
          <h2 id="titulo-popup-numeros" className="text-xl font-semibold">Números Operacionais</h2>
          <Button variant="ghost" size="icon" aria-label="Fechar Números" onClick={onClose} disabled={ocupado || Boolean(numeroRemocao)}><X className="h-5 w-5" /></Button>
        </div>
        {carregando ? <LoadingIndicator className="py-8">Carregando números...</LoadingIndicator> : falhaCarregamento ? (
          <p role="alert" className="mt-5 text-sm text-[var(--fz-field-error)]">{erro}</p>
        ) : (
          <div className="mt-5 space-y-4">
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted">Cadastre até {LIMITE} números operacionais.</p>
              <Button type="button" variant="outline" size="sm" onClick={adicionar} disabled={ocupado || numeros.length >= LIMITE || Boolean(numeroRemocao)} className="w-full sm:w-auto">Adicionar número</Button>
            </div>
            {numeros.length === 0 ? <p className="rounded-xl border border-dashed border-border px-4 py-3 text-sm text-muted">Nenhum número cadastrado.</p> : (
              <div className="divide-y divide-border overflow-hidden rounded-xl border border-border">
                {numeros.map((numero) => (
                  <div key={numero.posicao} className="flex items-center gap-3 px-3 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">Número {numero.posicao}</p>
                      {editando === numero.posicao ? (
                        <Input
                          type="tel"
                          inputMode="numeric"
                          value={numero.telefone}
                          onChange={(evento) => {
                            setNumeros((atuais) => atuais.map((atual) => atual.posicao === numero.posicao ? { ...atual, telefone: somenteDigitosTelefone(evento.target.value) } : atual));
                            setErro("");
                            setSucesso("");
                          }}
                          placeholder={numero.posicao === 1 ? "Obrigatório para ativar a separação" : "Opcional"}
                          aria-label={`Telefone do Número ${numero.posicao}`}
                          disabled={ocupado}
                          className="mt-1"
                        />
                      ) : <p className="mt-1 truncate text-sm text-muted">{numero.telefone || "Número sem telefone"}</p>}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button type="button" variant="ghost" size="icon" aria-label={`Editar Número ${numero.posicao}`} onClick={() => setEditando((atual) => atual === numero.posicao ? null : numero.posicao)} disabled={ocupado || Boolean(numeroRemocao)}><Pencil className="h-4 w-4" /></Button>
                      <Button type="button" variant="ghost" size="icon" aria-label={`Remover Número ${numero.posicao}`} onClick={() => void abrirRemocao(numero)} disabled={ocupado || carregandoRemocao || Boolean(numeroRemocao)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {numeros.length >= LIMITE ? <p className="text-xs text-muted">Limite de {LIMITE} números atingido.</p> : null}
            {erro ? <p role="alert" className="text-sm text-[var(--fz-field-error)]">{erro}</p> : null}
            {sucesso ? <p role="status" className="text-sm">{sucesso}</p> : null}
            <Button type="button" onClick={() => void salvar()} disabled={ocupado || Boolean(numeroRemocao) || (followPro && !numerosPendentes)} className="w-full">
              {salvando ? "Salvando..." : "Salvar números"}
            </Button>
            {followPro ? (
              <section className="space-y-3 border-t border-border pt-4" aria-label="Distribuição futura por número">
                <h3 className="font-semibold">Distribuição futura por número</h3>
                {numeros.length === 0 ? <p className="text-sm text-muted">Cadastre um número para configurar a distribuição.</p> : numeros.length === 1 ? (
                  <p className="text-sm">Número {numeros[0].posicao}: <strong>100%</strong> automaticamente.</p>
                ) : numerosPendentes ? (
                  <p className="text-sm text-muted">Salve as alterações dos números antes de definir os percentuais.</p>
                ) : carregandoPesos ? <LoadingIndicator className="py-4">Carregando percentuais...</LoadingIndicator> : falhaLeituraPesos ? (
                  <div className="space-y-2">
                    <p role="alert" className="text-sm text-[var(--fz-field-error)]">{erroPesos}</p>
                    <Button type="button" variant="outline" size="sm" onClick={() => void recarregarPercentuais()}>Recarregar percentuais</Button>
                  </div>
                ) : (
                  <>
                    {!configurada ? <p role="status" className="text-sm text-[var(--fz-field-error)]">Defina novamente os percentuais para distribuir novos Leads entre estes números.</p> : null}
                    <div className="space-y-3">
                      {numeros.map((numero) => (
                        <label key={numero.id} className="block space-y-1.5 text-sm font-medium">
                          Número {numero.posicao} ({numero.telefone})
                          <span className="flex items-center gap-2">
                            <Input
                              type="text"
                              inputMode="decimal"
                              value={rascunhos[numero.id] ?? ""}
                              onChange={(evento) => {
                                setRascunhos((atuais) => ({ ...atuais, [numero.id]: evento.target.value }));
                                setErroPesos("");
                                setSucessoPesos("");
                              }}
                              aria-label={`Percentual do Número ${numero.posicao}`}
                              placeholder="Ex.: 50"
                              disabled={ocupado}
                            />
                            <span aria-hidden="true">%</span>
                          </span>
                        </label>
                      ))}
                    </div>
                    <p className="text-sm">Soma: {exibirPercentual(somaPesos)}% de 100%</p>
                    {validacaoPesos && "erro" in validacaoPesos ? <p className="text-sm text-[var(--fz-field-error)]">{validacaoPesos.erro}</p> : null}
                    {erroPesos ? <p role="alert" className="text-sm text-[var(--fz-field-error)]">{erroPesos}</p> : null}
                    {sucessoPesos ? <p role="status" className="text-sm">{sucessoPesos}</p> : null}
                    <Button type="button" onClick={() => void salvarPercentuais()} disabled={ocupado || !validacaoPesos || "erro" in validacaoPesos} className="w-full">
                      {salvandoPesos ? "Salvando..." : "Salvar percentuais"}
                    </Button>
                  </>
                )}
              </section>
            ) : null}
          </div>
        )}
      </Dialog>

      {numeroRemocao ? (
        <Dialog
          open
          onOpenChange={(aberto) => { if (!aberto) fecharRemocao(); }}
          closeOnEscape={!removendo}
          closeOnBackdrop={!removendo}
          aria-labelledby="titulo-remocao-numero"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 id="titulo-remocao-numero" className="text-lg font-semibold">Remover Número {numeroRemocao.posicao}</h2>
              <p className="mt-1 text-sm text-muted">{numeroRemocao.telefone}</p>
            </div>
            <Button variant="ghost" size="icon" aria-label="Fechar remoção" onClick={fecharRemocao} disabled={removendo}><X className="h-5 w-5" /></Button>
          </div>
          <p className="mt-4 text-sm text-muted">{followPro ? "Confirme a remoção deste número. A distribuição futura pode exigir novos percentuais." : "Confira as Listas vinculadas antes de confirmar a remoção global."}</p>
          {carregandoRemocao ? <LoadingIndicator className="py-6">Carregando Listas...</LoadingIndicator> : listas.length ? (
            <div className="mt-4 space-y-2">
              {listas.map((lista) => (
                <div key={lista.listaLeadId} className="rounded-xl border border-border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">{lista.listaNome}</p>
                      <p className="text-xs text-muted">{lista.ativo ? "Ativo" : "Pausado"}</p>
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => { onClose(); router.push(`/leads/lista?listaId=${encodeURIComponent(lista.listaLeadId)}&aba=distribuicao`); }} disabled={removendo}>Abrir Lista</Button>
                  </div>
                  {lista.bloqueia ? <div className="mt-2 flex items-start gap-2 text-xs text-[var(--fz-field-error)]"><AlertTriangle className="h-4 w-4 shrink-0" /><div>{lista.motivos.map((motivo) => <p key={motivo}>{motivo}</p>)}</div></div> : null}
                </div>
              ))}
            </div>
          ) : !erroRemocao && !carregandoRemocao && !followPro ? <p className="mt-4 rounded-xl border border-dashed border-border p-3 text-sm text-muted">Este número não participa de nenhuma Lista.</p> : null}
          {listas.some((lista) => lista.bloqueia) ? <p className="mt-4 text-sm text-[var(--fz-field-error)]">Ajuste as Listas bloqueadoras antes de remover o número.</p> : null}
          {erroRemocao ? <p role="alert" className="mt-4 text-sm text-[var(--fz-field-error)]">{erroRemocao}</p> : null}
          <div className="mt-5 flex gap-2">
            <Button type="button" variant="outline" onClick={fecharRemocao} disabled={removendo} className="flex-1">Cancelar</Button>
            <Button type="button" variant="danger" onClick={() => void confirmarRemocao()} disabled={carregandoRemocao || removendo || Boolean(erroRemocao) || listas.some((lista) => lista.bloqueia)} className="flex-1">{removendo ? "Removendo..." : "Confirmar remoção"}</Button>
          </div>
        </Dialog>
      ) : null}
    </>
  );
}
